import { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { isTourActiveGlobal, isTourOrTransitioning } from '@/hooks/useTourController';
import { getOnboardingState, isHydrated } from '@/hooks/useOnboardingStateMachine';

// Cache auth state globally to prevent loading flash on navigation
let cachedUser: User | null = null;
let cachedSession: Session | null = null;
let authInitialized = false;

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Diagnostic logging
const logProtectedRoute = (event: string, data: Record<string, unknown>) => {
  console.log(`[FLIGHT] ProtectedRoute_${event}`, {
    ...data,
    file: 'ProtectedRoute.tsx',
    state: getOnboardingState(),
    hydrated: isHydrated(),
    tourActive: isTourActiveGlobal(),
    tourOrTransitioning: isTourOrTransitioning(),
    welcomeSeen: localStorage.getItem('therappi_welcome_seen_/') === 'true',
    tourSeen: localStorage.getItem('therappi-first-time-tour-seen') === 'true',
  });
};

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation();
  // Initialize with cached values to prevent flash on navigation
  const [user, setUser] = useState<User | null>(cachedUser);
  const [session, setSession] = useState<Session | null>(cachedSession);
  // Only show loading if we haven't initialized auth yet
  const [loading, setLoading] = useState(!authInitialized);
  
  const WHOOP_PATHS = ['/whoop-dashboard', '/whoop-callback'];
  const DEMO_EMAIL = 'support@therappi.app';


  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        // Update cache
        cachedSession = newSession;
        cachedUser = newSession?.user ?? null;
        authInitialized = true;
        
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);
        
        // Log authentication events for compliance
        if (newSession?.user && event === 'SIGNED_IN') {
          setTimeout(() => {
            supabase.rpc('log_therapy_action', {
              p_user_id: newSession.user.id,
              p_action: 'user_login',
              p_details: { timestamp: new Date().toISOString(), event }
            });
          }, 0);

          // Send notification for demo account login
          if (newSession.user.email === DEMO_EMAIL) {
            supabase.functions.invoke('notify-demo-login', {
              body: {
                email: newSession.user.email,
                timestamp: new Date().toISOString(),
                ip_address: 'Client-side (not available)',
                user_agent: navigator.userAgent
              }
            }).then(({ error }) => {
              if (error) {
                console.error('Failed to send login notification:', error);
              }
            });
          }
        }
      }
    );

    // THEN check for existing session (only if not already initialized)
    if (!authInitialized) {
      supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
        // Update cache
        cachedSession = existingSession;
        cachedUser = existingSession?.user ?? null;
        authInitialized = true;
        
        setSession(existingSession);
        setUser(existingSession?.user ?? null);
        setLoading(false);
      });
    }

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // CRITICAL: Never redirect while tour is active or transitioning
  if (isTourOrTransitioning()) {
    logProtectedRoute('REDIRECT_BLOCKED_TOUR_ACTIVE', {
      fromPath: location.pathname,
      reason: 'tour_or_transitioning_active',
      decision: 'BLOCKED - rendering children'
    });
    return <>{children}</>;
  }

  if (!user || !session) {
    // ======= DIAGNOSTIC: LOG BEFORE NAVIGATE =======
    logProtectedRoute('REDIRECT_TO_AUTH', {
      fromPath: location.pathname,
      toPath: '/auth',
      reason: 'no_user_or_session',
      hasUser: !!user,
      hasSession: !!session,
      decision: 'NAVIGATING_NOW'
    });
    return <Navigate to="/auth" replace />;
  }

  // If this is the demo user, restrict to WHOOP pages only
  if (user.email === DEMO_EMAIL && !WHOOP_PATHS.includes(location.pathname)) {
    logProtectedRoute('REDIRECT_DEMO_USER_TO_WHOOP', {
      fromPath: location.pathname,
      toPath: '/whoop-dashboard',
      reason: 'demo_user_restricted',
      decision: 'NAVIGATING_NOW'
    });
    return <Navigate to="/whoop-dashboard" replace />;
  }

  return <>{children}</>;
}